// src/Admin/GestionCertificados.js — Solponiente
// Gestión de las solicitudes de certificado: generar borrador -> firmar con
// AutoFirma -> entregar al usuario. Ciclo de vida propio, separado del de
// las solicitudes estándar (no pasa por "en proceso" ni genera Expediente).
import React, { useEffect, useState } from "react";
import { Table, Button, Badge, InputGroup, Form, Spinner, Modal } from "react-bootstrap";
import FirmaCertificado from "../FirmaCertificado";

const BASE_URL = "https://oficinavirtual-solponiente.com/api";

// Mapa de tipos de certificado disponibles. Añadir aquí una nueva entrada
// (y su función equivalente en el backend, ver CERTIFICADOS_DISPONIBLES en
// solicitudes/services.py) es lo único necesario para ofrecer más tipos en
// el futuro — no requiere cambios de esquema.
export const TIPOS_CERTIFICADO = {
  al_corriente_pago: "Certificado de estar al corriente de pago",
  comunidad_regantes: "Certificado de la Comunidad de Regantes",
};

const formatearFechaLarga = (iso) => {
  if (!iso) return "—";
  const [anio, mes, dia] = iso.split("-");
  if (!anio || !mes || !dia) return iso;
  const meses = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
    "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
  return `${parseInt(dia, 10)} de ${meses[parseInt(mes, 10) - 1]} de ${anio}`;
};

// Muestra el JSON guardado en DatosCertificado de forma legible, para que el
// admin pueda revisar lo que ha enviado el usuario antes de generar el
// borrador del certificado (p.ej. la tabla de recintos SIGPAC).
const DatosCertificadoModal = ({ solicitud, onHide }) => {
  if (!solicitud) return null;
  const datos = solicitud.DatosCertificado || {};
  const representacion = datos.representacion || {};
  const parcelas = datos.parcelas || [];

  return (
    <Modal show onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Datos de la solicitud — {solicitud.NumeroRegistro}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          <strong>Actúa en representación de una entidad:</strong>{" "}
          {representacion.activa ? "Sí" : "No"}
        </p>
        {representacion.activa && (
          <>
            <p className="mb-2"><strong>Entidad:</strong> {representacion.entidad_nombre || "—"}</p>
            <p className="mb-2"><strong>NIF entidad:</strong> {representacion.entidad_nif || "—"}</p>
          </>
        )}
        <p className="mb-2">
          <strong>Miembro de la Comunidad desde:</strong> {formatearFechaLarga(datos.miembro_desde)}
        </p>
        <p className="mb-3">
          <strong>Año de publicación SIGPAC:</strong> {datos.sigpac_anio || "—"}
        </p>

        <strong>Recintos SIGPAC:</strong>
        {parcelas.length === 0 ? (
          <p className="text-muted mt-2">No se han indicado recintos.</p>
        ) : (
          <div className="table-responsive mt-2">
            <Table bordered size="sm">
              <thead className="thead-brand thead-label">
                <tr>
                  <th>Provincia</th><th>Municipio</th><th>Polígono</th><th>Parcela</th>
                  <th>Recinto</th><th>Sup. total (ha)</th><th>Sup. riego (ha)</th><th>Dotación (hm³/ha)</th>
                </tr>
              </thead>
              <tbody>
                {parcelas.map((p, i) => (
                  <tr key={i}>
                    <td>{p.provincia}</td><td>{p.municipio}</td><td>{p.poligono}</td><td>{p.parcela}</td>
                    <td>{p.recinto}</td><td>{p.superficie_total}</td><td>{p.superficie_riego}</td><td>{p.dotacion}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {solicitud.Descripcion && (
          <>
            <strong>Observaciones:</strong>
            <p className="mb-0">{solicitud.Descripcion}</p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cerrar</Button>
      </Modal.Footer>
    </Modal>
  );
};

const GestionCertificados = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(null);
  const [descargando, setDescargando] = useState(null);
  const [verDatosDe, setVerDatosDe] = useState(null);
  const token = localStorage.getItem("token");

  const fetchSolicitudes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/solicitudes/completas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSolicitudes(data.filter((s) => s.TipoSolicitud === "certificado"));
      }
    } catch (e) {
      console.error("Error al obtener las solicitudes de certificado:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSolicitudes();
  }, []);

  const generarBorrador = async (solicitudId) => {
    setGenerando(solicitudId);
    try {
      const res = await fetch(`${BASE_URL}/solicitudes/${solicitudId}/generar-certificado`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        await fetchSolicitudes();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || "Error al generar el borrador del certificado.");
      }
    } catch (e) {
      alert("No se pudo conectar con el servidor.");
    } finally {
      setGenerando(null);
    }
  };

  const descargar = async (documentoId, nombre) => {
    setDescargando(documentoId);
    try {
      const res = await fetch(`${BASE_URL}/documentos/${documentoId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = nombre || "certificado.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      alert("No se pudo descargar el documento.");
    } finally {
      setDescargando(null);
    }
  };

  const renderEstado = (sol) => {
    if (sol.FirmadoAdmin) return <Badge bg="success">Enviado</Badge>;
    if (sol.DocumentoID) return <Badge bg="warning" text="dark">Borrador pendiente de firma</Badge>;
    return <Badge bg="secondary">Solicitado</Badge>;
  };

  const solicitudesFiltradas = solicitudes.filter((s) => {
    const term = filtro.toLowerCase();
    return (
      s.NumeroRegistro?.toLowerCase().includes(term) ||
      s.Usuario?.Nombre?.toLowerCase().includes(term) ||
      s.Usuario?.Apellidos?.toLowerCase().includes(term) ||
      s.Usuario?.Num_Identificacion?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="mt-2">
      <InputGroup className="mb-3">
        <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
        <Form.Control
          placeholder="Buscar por número de registro, nombre o identificación…"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
        {filtro && <Button variant="outline-secondary" onClick={() => setFiltro("")}>✕</Button>}
      </InputGroup>

      {loading ? (
        <p className="text-muted">Cargando solicitudes de certificado…</p>
      ) : solicitudesFiltradas.length === 0 ? (
        <p className="text-muted">No hay solicitudes de certificado.</p>
      ) : (
        <div className="table-responsive">
          <Table hover className="align-middle">
            <thead className="thead-brand thead-label">
              <tr>
                <th>Nº Registro</th>
                <th>Usuario</th>
                <th>Identificación</th>
                <th>Tipo de certificado</th>
                <th>Fecha</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {solicitudesFiltradas.map((sol) => (
                <tr key={sol.SolicitudID}>
                  <td>
                    <code className="text-primary fw-bold">{sol.NumeroRegistro}</code>
                    {!sol.Visto && (
                      <span className="badge bg-danger rounded-pill ms-2">Nueva</span>
                    )}
                  </td>
                  <td>{sol.Usuario?.Nombre} {sol.Usuario?.Apellidos}</td>
                  <td>{sol.Usuario?.Num_Identificacion}</td>
                  <td><small>{TIPOS_CERTIFICADO[sol.TipoCertificado] || sol.TipoCertificado}</small></td>
                  <td>
                    <small>
                      {sol.FechaSolicitud
                        ? new Date(sol.FechaSolicitud).toLocaleDateString("es-ES")
                        : "—"}
                    </small>
                  </td>
                  <td>{renderEstado(sol)}</td>
                  <td>
                    <div className="d-flex flex-column gap-1" style={{ minWidth: 170 }}>
                      {sol.DatosCertificado && (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => setVerDatosDe(sol)}
                        >
                          <i className="bi bi-eye me-1"></i>Ver datos
                        </Button>
                      )}

                      {!sol.DocumentoID && (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => generarBorrador(sol.SolicitudID)}
                          disabled={generando === sol.SolicitudID}
                        >
                          {generando === sol.SolicitudID ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <><i className="bi bi-file-earmark-plus me-1"></i>Generar borrador</>
                          )}
                        </Button>
                      )}

                      {sol.DocumentoID && !sol.FirmadoAdmin && (
                        <>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => descargar(sol.DocumentoID, sol.NombreDocumento)}
                            disabled={descargando === sol.DocumentoID}
                          >
                            <i className="bi bi-download me-1"></i>Ver borrador
                          </Button>
                          <FirmaCertificado
                            documentoId={sol.DocumentoID}
                            solicitudId={sol.SolicitudID}
                            onFirmado={fetchSolicitudes}
                          />
                        </>
                      )}

                      {sol.FirmadoAdmin && (
                        <Button
                          size="sm"
                          variant="outline-success"
                          onClick={() => descargar(sol.DocumentoID, sol.NombreDocumento)}
                          disabled={descargando === sol.DocumentoID}
                        >
                          <i className="bi bi-download me-1"></i>Descargar firmado
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <DatosCertificadoModal solicitud={verDatosDe} onHide={() => setVerDatosDe(null)} />
    </div>
  );
};

export default GestionCertificados;
